import { Children, type CSSProperties, type ReactNode } from 'react';
import { Toast } from '@base-ui/react/toast';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconLoader,
  IconX,
} from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import * as m from '@/paraglide/messages.js';

export const toastManager = Toast.createToastManager<{ actionHref?: string }>();

export function AppNotifications({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider toastManager={toastManager} timeout={5000} limit={3}>
      {children}
      <ToastList />
    </Toast.Provider>
  );
}

function ToastList() {
  const { toasts } = Toast.useToastManager();
  const visibleToasts = toasts.filter((toast) => !toast.limited);
  const activeToasts = visibleToasts.filter((toast) => toast.transitionStatus !== 'ending');
  const frontToast = activeToasts[0];
  const stackHeight = activeToasts.reduce((height, toast) => height + (toast.height ?? 0), 0);

  return (
    <Toast.Portal>
      <Toast.Viewport
        className="notice-viewport"
        aria-label={m.common_notifications()}
        data-empty={visibleToasts.length === 0 ? '' : undefined}
        style={
          {
            '--toast-frontmost-height': `${frontToast?.height ?? 0}px`,
            '--notice-stack-height': `${stackHeight}px`,
            '--notice-gaps': Math.max(0, activeToasts.length - 1),
          } as CSSProperties
        }
      >
        {toasts.map((toast, index) => (
          <Toast.Root
            key={toast.id}
            toast={toast}
            className="notice-toast"
            data-behind={
              (toast.transitionStatus === 'ending' ? index > 0 : toast.id !== frontToast?.id)
                ? ''
                : undefined
            }
            swipeDirection="right"
            onPointerDown={(event) => {
              const viewport = event.currentTarget.parentElement;
              const content = event.currentTarget.firstElementChild;
              const scrollable =
                (content && content.scrollHeight > content.clientHeight) ||
                (viewport && viewport.scrollHeight > viewport.clientHeight);
              // Keep Base UI's touch timer pause while skipping swipe capture for scrolling.
              event.currentTarget.toggleAttribute('data-base-ui-swipe-ignore', Boolean(scrollable));
            }}
          >
            <Toast.Content className="notice-content" tabIndex={0}>
              <div className="flex min-w-0 items-start gap-2">
                <ToastIcon type={toast.type} />
                <Toast.Title className="notice-title">
                  {Children.toArray(toast.title).some(
                    (child) => typeof child !== 'string' || child.trim().length > 0,
                  )
                    ? toast.title
                    : m.common_notifications()}
                </Toast.Title>
              </div>
              <Toast.Description className="notice-description" />
              {toast.actionProps && (
                <Toast.Action
                  {...toast.actionProps}
                  nativeButton={!toast.data?.actionHref}
                  role={toast.data?.actionHref ? 'link' : undefined}
                  render={
                    <Button
                      variant="outline"
                      treatment="wrap"
                      nativeButton={!toast.data?.actionHref}
                      role={toast.data?.actionHref ? 'link' : undefined}
                      render={
                        toast.data?.actionHref ? (
                          <a href={toast.data.actionHref}>{toast.actionProps.children}</a>
                        ) : undefined
                      }
                    />
                  }
                />
              )}
            </Toast.Content>
            <Toast.Close
              className="absolute top-2 right-2"
              aria-hidden={false}
              aria-label={m.common_dismiss_notification()}
              render={<Button variant="ghost" tone="muted" size="icon-sm" radius="full" />}
            >
              <IconX aria-hidden="true" />
            </Toast.Close>
          </Toast.Root>
        ))}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

const toastIcons: Record<string, typeof IconInfoCircle> = {
  error: IconAlertCircle,
  info: IconInfoCircle,
  loading: IconLoader,
  success: IconCircleCheck,
  warning: IconAlertTriangle,
};

function ToastIcon({ type }: { type: string | undefined }) {
  const Icon = (type && toastIcons[type]) || IconInfoCircle;
  return (
    <Icon
      aria-hidden="true"
      className="notice-icon in-data-[type=loading]:animate-spin motion-reduce:animate-none"
    />
  );
}
